import { useEffect, useState } from "react";
import { Loader2, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { downloadCSV } from "../../lib/csv";

const COLS = [["efectivo", "Efectivo"], ["tarjeta", "Tarjeta"], ["transferencia", "Transferencia"], ["web", "Web"], ["ventas", "Vendido"], ["anulado", "Anulado"], ["neto", "Neto a liquidar"]];
const thisMonth = () => new Date(Date.now() - 3 * 36e5).toISOString().slice(0, 7);

const Line = ({ x, total }) => (
  <tr data-testid={total ? "monthly-total-row" : `monthly-row-${x.key}`} className={total ? "border-t-2 border-black font-bold" : "border-b border-black/10"}>
    <td className="py-2 pr-3">{total ? "Total" : x.office}</td>
    {COLS.map(([k]) => <td key={k} className={`py-2 pr-3 text-right ${k === "anulado" && x[k] ? "text-red-700" : ""} ${k === "neto" ? "font-bold" : ""}`}>{k === "anulado" && x[k] ? `− ${formatARS(x[k])}` : formatARS(x[k])}</td>)}
    <td className="py-2 pr-3 text-right">{x.pasajes}</td>
    <td className="py-2 text-right">{x.cierres}{x.diferencia ? <span className={`block text-xs ${x.diferencia < 0 ? "text-red-700" : ""}`}>dif. {formatARS(x.diferencia)}</span> : null}</td>
  </tr>
);

const RCOLS = [["reservas", "Reservas"], ["cobradas", "Cobradas"], ["cobradas_aca", "Cobradas acá"], ["vencidas", "Vencidas"], ["anuladas", "Anuladas"], ["pendientes", "Pendientes"]];
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");

const Reservations = ({ rows, totals }) => (
  <div className="mt-8 break-inside-avoid">
    <h3 className="font-display-up text-lg">Reservas por boletería</h3>
    {rows.length === 0 ? <p className="mt-2 text-sm text-[var(--muted-ink)]">No hubo reservas este mes.</p> : (
      <div className="mt-2 overflow-x-auto">
        <table data-testid="monthly-reservations" className="w-full min-w-[760px] border-collapse text-sm">
          <thead><tr className="border-b-2 border-black text-left text-xs"><th className="py-2">Reservó</th>{RCOLS.map(([k, l]) => <th key={k} className="py-2 pr-3 text-right">{l}</th>)}<th className="py-2 pr-3 text-right">% cobradas</th><th className="py-2 text-right">Monto cobrado</th></tr></thead>
          <tbody>
            {[...rows, { ...totals, key: "total", office: "Total" }].map((x) => (
              <tr key={x.key} data-testid={`monthly-res-${x.key}`} className={x.key === "total" ? "border-t-2 border-black font-bold" : "border-b border-black/10"}>
                <td className="py-2 pr-3">{x.office}</td>
                {RCOLS.map(([k]) => <td key={k} className={`py-2 pr-3 text-right ${k === "vencidas" && x[k] ? "text-red-700" : ""}`}>{x[k] ?? 0}</td>)}
                <td className="py-2 pr-3 text-right">{pct(x.cobradas, x.reservas)}</td><td className="py-2 text-right">{formatARS(x.monto_cobrado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <p className="mt-2 text-xs text-[var(--muted-ink)]">Según la boletería que hizo la reserva. “Cobradas acá” = cobradas en la misma boletería; el resto se cobró en otra.</p>
  </div>
);

export const MonthlyPanel = () => {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/admin/sales/monthly", { params: { month } }).then((r) => setD({ items: Array.isArray(r.data?.items) ? r.data.items : [], totals: r.data?.totals ?? {}, reservations: Array.isArray(r.data?.reservations) ? r.data.reservations : [], reservations_totals: r.data?.reservations_totals ?? {} })).catch((e) => toast.error(apiError(e)));
  }, [month]);
  const csv = () => downloadCSV(`liquidacion_${month}.csv`, [["Boletería", ...COLS.map((c) => c[1]), "Pasajes", "Cierres", "Diferencia cierres"],
    ...d.items.map((x) => [x.office, ...COLS.map(([k]) => x[k]), x.pasajes, x.cierres, x.diferencia]), ["Total", ...COLS.map(([k]) => d.totals[k]), d.totals.pasajes, d.totals.cierres, d.totals.diferencia]]);

  return (
    <div data-testid="monthly-panel">
      <div className="flex flex-wrap items-center gap-2 text-sm print:hidden">
        Mes <Input data-testid="monthly-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-auto" />
        <span className="ml-auto flex gap-2">
          {d?.items.length > 0 && <button onClick={csv} data-testid="monthly-csv" className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white"><Download className="h-4 w-4" /> Excel (CSV)</button>}
          <button onClick={() => window.print()} data-testid="monthly-print" className="inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-4 py-2 text-sm font-bold text-white"><Printer className="h-4 w-4" /> Imprimir</button>
        </span>
      </div>
      {!d ? <Loader2 className="mt-6 h-5 w-5 animate-spin" /> : (
        <div className="mt-5 rounded-2xl bg-white p-5 text-black ring-1 ring-black/5 md:p-8 print:p-0 print:ring-0">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-black pb-3">
            <div><img src="/brand/logo-green.png" alt="Santiagueño" className="h-10 w-auto" /><h2 className="mt-2 font-display text-3xl">Liquidación mensual</h2></div>
            <p data-testid="monthly-label" className="text-sm font-semibold">{month} · cobrado por cada boletería</p>
          </div>
          {d.items.length === 0 ? <p className="mt-6 text-[var(--muted-ink)]">No hubo ventas cobradas en este mes.</p> : (
            <div className="mt-4 overflow-x-auto">
              <table data-testid="monthly-table" className="w-full min-w-[900px] border-collapse text-sm">
                <thead><tr className="border-b-2 border-black text-left text-xs"><th className="py-2">Boletería</th>{COLS.map(([k, l]) => <th key={k} className="py-2 pr-3 text-right">{l}</th>)}<th className="py-2 pr-3 text-right">Pasajes</th><th className="py-2 text-right">Cierres</th></tr></thead>
                <tbody>{d.items.map((x) => <Line key={x.key} x={x} />)}<Line x={d.totals} total /></tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs text-[var(--muted-ink)]">Cada venta cuenta en la boletería que la cobró (incluye reservas cobradas en otra boletería). Anulado = devoluciones hechas en el mes. “Web” son pagos online con tarjeta (NAVE).</p>
          <Reservations rows={d.reservations} totals={d.reservations_totals} />
        </div>
      )}
    </div>
  );
};
